import React, { useRef } from "react";
import PropTypes from "prop-types";
import {
  Text,
  TextInput,
  Flex,
  Box,
  Image,
  useMantineTheme,
} from "@mantine/core";

import { useStylesGeneral } from "../utils/mantine/stampTabContent/useStylesGeneral";
import { useStylesButton } from "../utils/mantine/useStylesButton";
import { useStylesLanding } from "../utils/mantine/useStylesLanding";
import CustomTooltip from "./mantineCustomComponents/CustomTooltip";
import CustomAnchor from "./mantineCustomComponents/CustomAnchor";
import { useStylesTooltip } from "../utils/mantine/useStylesTooltip";
import CustomButton from "./mantineCustomComponents/CustomButton";
import {
  HASH_ALGORITHM_SHA3_256,
  HASH_ALGORITHM_KECCAK_256,
} from "../utils/cryptoUtils";

const VerifyFile = ({ handleFileInput, staticBasePath, helpIsActive }) => {
  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  const { classes: GeneralClasses } = useStylesGeneral();
  const { classes: buttonClasses } = useStylesButton();
  const { classes: landingClasses } = useStylesLanding();
  const { classes: tooltipClasses } = useStylesTooltip();

  const fileInputRef = useRef(null);
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box id="verify-method-user-found__screen" className={GeneralClasses.wrap}>
      <Box className={GeneralClasses.wrapContent}>
        <Flex
          direction="column"
          justify={theme.flex.justify.start}
          align={theme.flex.align.center}
          gap={theme.flex.gap.lg}
          id="drop-area"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleFileInput}
          classNames={{
            root: `${GeneralClasses.wrapContentContainer} ${GeneralClasses.wrapContentContainerRelative}`,
          }}
        >
          <Box className={GeneralClasses.wrapContentContainerPadding}>
            {helpIsActive && (
              <CustomTooltip
                classNames="tooltip-top"
                content={
                  <Text
                    classNames={{
                      root: tooltipClasses.tooltipText,
                    }}
                  >
                    Selecting a file calculates its digital fingerprint (aka:
                    ContentID or hash). Uses {HASH_ALGORITHM_SHA3_256} for new
                    stamps; {HASH_ALGORITHM_KECCAK_256} supported for
                    verification. This calculation is done inside your browser,
                    the file is never transmitted anywhere outside your
                    computer.
                    <Box>
                      <CustomAnchor
                        text="Learn more"
                        href="https://docs.vbase.com/getting-started/web-tools/how-to-use-vbase-stamper"
                        componentClasses="link"
                      />
                    </Box>
                  </Text>
                }
              ></CustomTooltip>
            )}

            <CustomButton
              text="Browse for file"
              id="fileOpenTrigger"
              icon="IconVerifyFromFile"
              onClick={handleClick}
              componentClasses={`btn-verify ${buttonClasses.buttonBlack} ${landingClasses.pricingBlockButtonsButton} ${GeneralClasses.buttonFile}`}
            />
            <Text component="div" className={GeneralClasses.fileDropzoneText}>
              Or just drag and drop file here
            </Text>
          </Box>
        </Flex>
        <Flex
          align={theme.flex.align.center}
          justify={theme.flex.justify.center}
          className={GeneralClasses.infoFileTab}
        >
          <Image
            src={`${staticBasePath}public/images/svg/private_local.svg`}
            alt="Info"
          />
          <Text pr={theme.padding.smEm}>
            The file is processed locally on your computer. It is never sent to
            vBase or anywhere else.
          </Text>
          {helpIsActive && (
            <CustomTooltip
              classNames="tooltip-Bottom"
              content={
                <Text
                  classNames={{
                    root: tooltipClasses.tooltipText,
                  }}
                >
                  It’s your data, and privacy is important. The vBase Stamper
                  works without transmitting your data.
                </Text>
              }
            ></CustomTooltip>
          )}
        </Flex>
        <TextInput
          type="file"
          id="fileInput"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileInput}
        />
      </Box>
    </Box>
  );
};
VerifyFile.propTypes = {
  staticBasePath: PropTypes.string.isRequired,
  handleFileInput: PropTypes.func.isRequired,
  helpIsActive: PropTypes.bool.isRequired,
};
export default VerifyFile;
